// =============================================================================
// THEME SWITCHER COMPONENT
// =============================================================================

import React, { useState } from 'react';
import { Palette, Sun, Moon, Contrast, Droplet } from 'lucide-react';
import { useTheme } from '@/hooks/useTheme';
import { Button } from '@/components/Button';
import { cn } from '@/utils';

export const ThemeSwitcher: React.FC = () => {
  const {
    theme,
    setTheme,
    setSystemTheme,
    isSystemTheme,
    themes,
    getThemeDisplayName,
    isActiveTheme,
  } = useTheme();

  const [isOpen, setIsOpen] = useState(false);

  const handleSystemThemeClick = () => {
    setSystemTheme();
    setIsOpen(false);
  };

  const getThemeIcon = (themeName: string) => {
    switch (themeName) {
      case 'light':
        return <Sun size={16} />;
      case 'dark':
        return <Moon size={16} />;
      case 'high-contrast':
        return <Contrast size={16} />;
      default:
        return <Droplet size={16} />;
    }
  };

  const currentIcon = getThemeIcon(theme);

  return (
    <div className="theme-switcher">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
        className={cn(isOpen && 'active')}
        aria-label="Toggle theme menu"
      >
        {currentIcon}
      </Button>

      {isOpen && (
        <div className="theme-menu">
          <div className="theme-menu-header">
            <h4>Choose Theme</h4>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsOpen(false)}
              aria-label="Close theme menu"
            >
              ×
            </Button>
          </div>
          
          <div className="theme-options">
            {themes.map((themeName) => (
              <Button
                key={themeName}
                variant={isActiveTheme(themeName) ? 'primary' : 'ghost'}
                size="sm"
                onClick={() => {
                  setTheme(themeName);
                  setIsOpen(false);
                }}
                className="theme-option"
              >
                {getThemeIcon(themeName)}
                <span>{getThemeDisplayName(themeName)}</span>
              </Button>
            ))}
            
            <Button
              variant={isSystemTheme ? 'primary' : 'ghost'}
              size="sm"
              onClick={handleSystemThemeClick}
              className="theme-option"
            >
              <Palette size={16} />
              <span>System</span>
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
